import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UsersThree,
  Plus,
  PencilSimple,
  Trash,
  X,
  Phone,
  EnvelopeSimple,
  ChatCircleText,
  ChatCircleDots,
  CalendarCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import * as API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import ItemMenu from "../components/ItemMenu";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  relationship: "",
  notes: "",
  sms_authorized: false,
  can_manage_schedule: false,
  aliases: [],
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (new) | contact (edit)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await API.listContacts();
      setContacts(list);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove ${c.name} from contacts?`)) return;
    try {
      await API.deleteContact(c.id);
      toast.success("Contact removed");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="contacts-page">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UsersThree size={22} className="text-cyan-300" weight="duotone" />
            <h1 className="font-display text-2xl">Contacts</h1>
          </div>
          <p className="text-sm text-white/55 max-w-lg">
            People Journey can text on your behalf. Names here power
            phrases like "text Lynn" or "remind Maya tomorrow at 8 AM."
          </p>
        </div>
        <Button
          onClick={() => setEditing({})}
          className="bg-cyan-500/90 hover:bg-cyan-500 text-black font-medium shrink-0"
          data-testid="contacts-add-button"
        >
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </header>

      {loading && contacts.length === 0 ? (
        <p className="text-sm text-white/40">Loading…</p>
      ) : contacts.length === 0 ? (
        <EmptyState onAdd={() => setEditing({})} />
      ) : (
        <ul className="space-y-2" data-testid="contacts-list">
          {contacts.map((c) => (
            <ContactRow
              key={c.id}
              c={c}
              onEdit={() => setEditing(c)}
              onDelete={() => handleDelete(c)}
            />
          ))}
        </ul>
      )}

      <AnimatePresence>
        {editing && (
          <ContactDialog
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              setEditing(null);
              await load();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ContactRow({ c, onEdit, onDelete }) {
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white/[0.03] hover:bg-white/[0.05] border border-white/5 rounded-2xl p-4 flex items-start gap-3 transition-colors"
      data-testid={`contact-row-${c.id}`}
    >
      <Avatar name={c.name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-white truncate">{c.name}</span>
          {c.relationship && (
            <span className="text-xs text-cyan-300/80 truncate">
              · {c.relationship}
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
          {c.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone size={12} weight="duotone" /> {c.phone}
            </span>
          )}
          {c.email && (
            <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
              <EnvelopeSimple size={12} weight="duotone" /> {c.email}
            </span>
          )}
          {c.sms_authorized && (
            <span
              className="inline-flex items-center gap-1 text-cyan-300/90"
              data-testid={`contact-sms-badge-${c.id}`}
            >
              <ChatCircleDots size={12} weight="fill" /> Can chat via SMS
            </span>
          )}
          {c.sms_authorized && c.can_manage_schedule && (
            <span
              className="inline-flex items-center gap-1 text-amber-300/90"
              data-testid={`contact-schedule-badge-${c.id}`}
            >
              <CalendarCheck size={12} weight="fill" /> Can manage schedule
            </span>
          )}
          {!c.phone && !c.email && (
            <span className="text-white/30">no phone or email yet</span>
          )}
        </div>
        {c.notes && (
          <p className="mt-2 text-xs text-white/40 line-clamp-2">{c.notes}</p>
        )}
        {Array.isArray(c.aliases) && c.aliases.length > 0 && (
          <div
            className="mt-2 flex flex-wrap gap-1"
            data-testid={`contact-aliases-row-${c.id}`}
          >
            {c.aliases.map((a, i) => (
              <span
                key={`${a}-${i}`}
                className="text-[10.5px] px-1.5 py-0.5 rounded-full bg-white/[0.04] text-white/55 border border-white/[0.06]"
              >
                {a}
              </span>
            ))}
          </div>
        )}
      </div>
      <ItemMenu
        testId={`contact-menu-${c.id}`}
        items={[
          { label: "Edit", icon: PencilSimple, onClick: onEdit, testId: `contact-edit-${c.id}` },
          { label: "Delete", icon: Trash, onClick: onDelete, danger: true, testId: `contact-delete-${c.id}` },
        ]}
      />
    </motion.li>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-cyan-400/30 to-cyan-700/10 border border-cyan-200/10 flex items-center justify-center text-cyan-100 text-sm font-medium">
      {initials || "?"}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div
      className="text-center py-12 px-4 border border-white/10 rounded-2xl bg-white/[0.02]"
      data-testid="contacts-empty"
    >
      <ChatCircleText
        size={32}
        weight="duotone"
        className="mx-auto text-cyan-300/80 mb-3"
      />
      <p className="text-white/80 font-medium">No contacts yet</p>
      <p className="text-sm text-white/50 mt-1 max-w-sm mx-auto">
        Add someone you'd like Journey to text on your behalf. Then say
        things like "text Lynn 'on my way'" in chat.
      </p>
      <Button
        onClick={onAdd}
        className="mt-4 bg-cyan-500/90 hover:bg-cyan-500 text-black font-medium"
        data-testid="contacts-empty-add"
      >
        <Plus size={16} className="mr-1" /> Add your first contact
      </Button>
    </div>
  );
}

function ContactDialog({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    ...EMPTY,
    ...(initial || {}),
    aliases: Array.isArray(initial?.aliases) ? [...initial.aliases] : [],
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        relationship: form.relationship.trim(),
        notes: form.notes.trim(),
        sms_authorized: !!form.sms_authorized,
        can_manage_schedule: !!form.sms_authorized && !!form.can_manage_schedule,
        aliases: (form.aliases || [])
          .map((a) => (a || "").trim())
          .filter(Boolean),
      };
      if (isEdit) {
        await API.updateContact(initial.id, payload);
        toast.success("Contact updated");
      } else {
        await API.createContact(payload);
        toast.success("Contact added");
      }
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="contact-dialog"
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f0f0f] border border-white/10 w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"
      >
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-medium">
              {isEdit ? "Edit contact" : "New contact"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 -m-1 text-white/50 hover:text-white"
              aria-label="Close"
              data-testid="contact-dialog-close"
            >
              <X size={18} />
            </button>
          </div>

          <Field label="Name *">
            <Input
              autoFocus
              value={form.name}
              onChange={update("name")}
              placeholder="Lynn"
              className="bg-transparent border-white/10"
              data-testid="contact-name-input"
            />
          </Field>

          <Field
            label="Phone"
            hint="E.164 (+13235551234) or bare 10-digit US. Required for texting."
          >
            <Input
              value={form.phone}
              onChange={update("phone")}
              placeholder="+13235551234"
              type="tel"
              className="bg-transparent border-white/10"
              data-testid="contact-phone-input"
            />
          </Field>

          <Field label="Relationship" hint="Optional. Shows up in lists.">
            <Input
              value={form.relationship}
              onChange={update("relationship")}
              placeholder="Sister · Coworker · Plumber"
              className="bg-transparent border-white/10"
              data-testid="contact-relationship-input"
            />
          </Field>

          <Field
            label="Aliases / nicknames"
            hint='Other names you call them. Journey resolves "Lil Bro", "Brother", etc. to this contact. Press Enter or comma to add. Up to 10.'
          >
            <AliasChips
              aliases={form.aliases}
              onChange={(next) => setForm((f) => ({ ...f, aliases: next }))}
            />
          </Field>

          <Field label="Email">
            <Input
              value={form.email}
              onChange={update("email")}
              placeholder="lynn@example.com"
              type="email"
              className="bg-transparent border-white/10"
              data-testid="contact-email-input"
            />
          </Field>

          <Field label="Notes">
            <Textarea
              value={form.notes}
              onChange={update("notes")}
              placeholder="Anything Journey should remember when contacting them"
              className="bg-transparent border-white/10 min-h-[64px]"
              data-testid="contact-notes-input"
            />
          </Field>

          <div
            className="flex items-start gap-3 pt-1 pb-2 px-3 -mx-1 rounded-xl bg-white/[0.03] border border-white/[0.06]"
            data-testid="contact-sms-auth-row"
          >
            <Switch
              checked={!!form.sms_authorized}
              onCheckedChange={(v) =>
                setForm((f) => ({ ...f, sms_authorized: !!v }))
              }
              className="mt-1 data-[state=checked]:bg-cyan-500"
              data-testid="contact-sms-authorized-switch"
            />
            <div className="flex-1">
              <p className="text-sm text-white/90">Allow SMS chat</p>
              <p className="text-[11px] text-white/45 mt-0.5">
                If this contact texts Journey's number, Journey will
                reply conversationally on your behalf. Private data
                stays private — they only get casual chat.
              </p>
            </div>
          </div>

          <div
            className={`flex items-start gap-3 pt-1 pb-2 px-3 -mx-1 rounded-xl bg-white/[0.03] border border-white/[0.06] transition-opacity ${
              form.sms_authorized ? "" : "opacity-50"
            }`}
            data-testid="contact-schedule-row"
          >
            <Switch
              checked={!!form.sms_authorized && !!form.can_manage_schedule}
              disabled={!form.sms_authorized}
              onCheckedChange={(v) =>
                setForm((f) => ({ ...f, can_manage_schedule: !!v }))
              }
              className="mt-1 data-[state=checked]:bg-amber-500"
              data-testid="contact-can-manage-schedule-switch"
            />
            <div className="flex-1">
              <p className="text-sm text-white/90">Can manage schedule</p>
              <p className="text-[11px] text-white/45 mt-0.5">
                They can text Journey to add, change or cancel reminders
                they set or that are for them. Changes need their YES and
                you get a notification. Your own reminders stay hidden.
                {!form.sms_authorized && " Turn on SMS chat first."}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-white/70 hover:text-white"
              data-testid="contact-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-cyan-500/90 hover:bg-cyan-500 text-black font-medium"
              data-testid="contact-save-button"
            >
              {saving ? "Saving…" : isEdit ? "Save" : "Add contact"}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs text-white/55 mb-1 block">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-white/35 mt-1">{hint}</p>}
    </div>
  );
}

const MAX_ALIASES = 10;
const MAX_ALIAS_LEN = 40;

function AliasChips({ aliases, onChange }) {
  const [draft, setDraft] = useState("");

  const commit = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (v.length > MAX_ALIAS_LEN) {
      toast.error(`Alias too long (${MAX_ALIAS_LEN} char max)`);
      return;
    }
    const existing = (aliases || []).map((a) => a.toLowerCase());
    if (existing.includes(v.toLowerCase())) return;
    if ((aliases || []).length >= MAX_ALIASES) {
      toast.error(`Up to ${MAX_ALIASES} aliases`);
      return;
    }
    onChange([...(aliases || []), v]);
    setDraft("");
  };

  const remove = (idx) => {
    const next = [...(aliases || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && (aliases || []).length) {
      remove((aliases || []).length - 1);
    }
  };

  return (
    <div
      className="bg-transparent border border-white/10 rounded-md px-2 py-1.5 flex flex-wrap gap-1.5"
      data-testid="contact-aliases-field"
    >
      {(aliases || []).map((a, i) => (
        <span
          key={`${a}-${i}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-200 border border-cyan-300/20"
          data-testid={`contact-alias-chip-${i}`}
        >
          {a}
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-cyan-200/70 hover:text-white"
            aria-label={`Remove ${a}`}
            data-testid={`contact-alias-remove-${i}`}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => commit(draft)}
        placeholder={(aliases || []).length ? "" : "Brother, Lil Bro, …"}
        className="flex-1 min-w-[110px] bg-transparent text-sm text-white outline-none py-0.5 px-1"
        data-testid="contact-alias-input"
      />
    </div>
  );
}
